import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TipoLineaMaterial } from '@cmms/shared';
import { api, consulta, ErrorApi } from '../servicios/api.js';
import { SelectorBuscable, SelectorSegmentado } from './selectores.js';
import { Dialogo, Aviso } from './piezas.js';

interface LineaPlan {
  id: string;
  catalogoId: string | null;
  nombre: string;
  tipo: TipoLineaMaterial;
  cantidad: number;
  nota: string | null;
  orden: number;
}

interface ElementoCatalogo {
  id: string;
  nombre: string;
  tipo: TipoLineaMaterial;
  tipoActivoId: string | null;
  sugerido: boolean;
}

/**
 * Línea del borrador. Si `catalogoId` es null, es un elemento nuevo: se registra
 * en el catálogo al guardar el plan.
 */
interface BorradorLinea {
  catalogoId: string | null;
  nombre: string;
  tipo: TipoLineaMaterial;
  cantidad: number;
  nota: string | null;
}

const ETIQUETA_TIPO: Record<TipoLineaMaterial, string> = {
  HERRAMIENTA: 'Herramienta',
  MATERIAL: 'Material',
};

/**
 * Plan de materiales y herramientas de una solicitud (0064). Quien aprueba la
 * solicitud lo arma aquí; al generar la OT, el plan pasa a ella tal cual. Con
 * `editable` false se muestra solo lectura (lo que ve el técnico en el alta).
 */
export function PlanMaterialesSolicitud({
  solicitudId,
  activoId,
  editable,
}: {
  solicitudId: string;
  activoId: string | null;
  editable: boolean;
}): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['plan-materiales', solicitudId],
    queryFn: () => api.get<LineaPlan[]>(`/solicitudes/${solicitudId}/materiales`),
  });

  if (isLoading) return <p className="campo-nota">Cargando materiales…</p>;
  if (error) {
    return <p className="campo-nota">{error instanceof ErrorApi ? error.message : 'No se pudo cargar el plan de materiales.'}</p>;
  }
  if (!data) return <></>;

  return (
    <EditorPlan
      key={solicitudId}
      urlPlan={`/solicitudes/${solicitudId}/materiales`}
      clavePlan={['plan-materiales', solicitudId]}
      activoId={activoId}
      editable={editable}
      inicial={data}
    />
  );
}

function aBorradores(lineas: LineaPlan[]): BorradorLinea[] {
  return lineas.map((l) => ({
    catalogoId: l.catalogoId ?? null,
    nombre: l.nombre,
    tipo: l.tipo,
    cantidad: l.cantidad,
    nota: l.nota,
  }));
}

export function EditorPlan({
  urlPlan,
  clavePlan,
  iniciarEnEdicion = false,
  alGuardar,
  sinPanel = false,
  activoId,
  editable,
  inicial,
}: {
  urlPlan: string;
  clavePlan: unknown[];
  iniciarEnEdicion?: boolean;
  alGuardar?: () => void;
  /** Dentro de un diálogo: sin el título ni el marco del panel, que ya los pone el diálogo. */
  sinPanel?: boolean;
  activoId: string | null;
  editable: boolean;
  inicial: LineaPlan[];
}): JSX.Element {
  const cliente = useQueryClient();
  const [lineas, setLineas] = useState<BorradorLinea[]>(() => aBorradores(inicial));
  // Último plan guardado: al cancelar la edición, el borrador vuelve a esto.
  const [guardadas, setGuardadas] = useState<BorradorLinea[]>(() => aBorradores(inicial));
  // Un plan ya guardado se ve en solo lectura hasta que se pulsa «Editar plan». Uno vacío arranca editable.
  const [enEdicion, setEnEdicion] = useState(editable && (iniciarEnEdicion || inicial.length === 0));
  const puedeEditar = editable && enEdicion;
  const [sucio, setSucio] = useState(false);
  const [creando, setCreando] = useState(false);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoTipo, setNuevoTipo] = useState<TipoLineaMaterial>('HERRAMIENTA');
  const [errorNuevo, setErrorNuevo] = useState<string | null>(null);

  const { data: catalogo, error: errorCatalogo } = useQuery({
    queryKey: ['materiales-catalogo', activoId ?? ''],
    queryFn: () => api.get<ElementoCatalogo[]>(`/materiales-catalogo${consulta({ activoId: activoId ?? undefined })}`),
    enabled: editable,
  });

  const guardar = useMutation({
    mutationFn: async () => {
      // Los elementos nuevos del borrador se registran en el catálogo antes de armar el plan.
      const registradas: BorradorLinea[] = [];
      for (const l of lineas) {
        if (l.catalogoId) {
          registradas.push(l);
          continue;
        }
        // Un material que ya está en el catálogo (p. ej. una línea copiada de la OT) se reutiliza, no se duplica.
        const enCatalogo = (catalogo ?? []).find((c) => c.nombre.trim().toLowerCase() === l.nombre.trim().toLowerCase());
        if (enCatalogo) {
          registradas.push({ ...l, catalogoId: enCatalogo.id, nombre: enCatalogo.nombre, tipo: enCatalogo.tipo });
          continue;
        }
        const creado = await api.post<ElementoCatalogo>('/materiales-catalogo', {
          nombre: l.nombre,
          tipo: l.tipo,
          tipoActivoId: null,
        });
        registradas.push({ ...l, catalogoId: creado.id, nombre: creado.nombre, tipo: creado.tipo });
      }
      return api.put<LineaPlan[]>(urlPlan, {
        lineas: registradas.map((l) => ({
          catalogoId: l.catalogoId,
          nombre: l.nombre,
          tipo: l.tipo,
          cantidad: l.cantidad,
          nota: l.nota,
        })),
      });
    },
    onSuccess: async (planGuardado) => {
      setLineas(aBorradores(planGuardado));
      setGuardadas(aBorradores(planGuardado));
      setSucio(false);
      setEnEdicion(false);
      await cliente.invalidateQueries({ queryKey: clavePlan });
      alGuardar?.();
      await cliente.invalidateQueries({ queryKey: ['materiales-catalogo'] });
    },
  });

  function agregar(elemento: ElementoCatalogo): void {
    setLineas((actuales) => [
      ...actuales,
      { catalogoId: elemento.id, nombre: elemento.nombre, tipo: elemento.tipo, cantidad: 1, nota: null },
    ]);
    setSucio(true);
  }

  function agregarNuevo(): void {
    const nombre = nuevoNombre.trim();
    if (!nombre) return setErrorNuevo('Escriba el nombre del material o herramienta.');
    const existe = (catalogo ?? []).some((e) => e.nombre.trim().toLowerCase() === nombre.toLowerCase());
    if (existe) return setErrorNuevo('Ya existe en el catálogo: búsquelo arriba y agréguelo.');
    setLineas((actuales) => [...actuales, { catalogoId: null, nombre, tipo: nuevoTipo, cantidad: 1, nota: null }]);
    setSucio(true);
    setNuevoNombre('');
    setErrorNuevo(null);
    setCreando(false);
  }

  function cancelarEdicion(): void {
    setLineas(guardadas);
    setSucio(false);
    setCreando(false);
    setErrorNuevo(null);
    setEnEdicion(false);
    guardar.reset();
  }

  function quitar(indice: number): void {
    setLineas((actuales) => actuales.filter((_, i) => i !== indice));
    setSucio(true);
  }

  function cambiarCantidad(indice: number, valor: string): void {
    setLineas((actuales) => actuales.map((l, i) => (i === indice ? { ...l, cantidad: Number(valor) } : l)));
    setSucio(true);
  }

  const opcionesCatalogo = (catalogo ?? []).filter((e) => !lineas.some((l) => l.catalogoId === e.id));
  const sugeridos = opcionesCatalogo.filter((e) => e.sugerido);
  const resto = opcionesCatalogo.filter((e) => !e.sugerido);

  return (
    <section className={sinPanel ? undefined : 'panel'} id="plan-materiales">
      {sinPanel ? null : (
        <div className="panel-cabecera" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <h2>Materiales y herramientas</h2>
          {editable && !enEdicion ? (
            <button type="button" className="boton secundario pequeno" onClick={() => setEnEdicion(true)}>
              Editar plan
            </button>
          ) : null}
        </div>
      )}
      <div className={sinPanel ? undefined : 'panel-cuerpo'}>
        {puedeEditar ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 }}>
            <div className="plan-materiales-buscar" style={{ flex: '1 1 260px', minWidth: 0 }}>
              <SelectorBuscable
                id="agregar-material"
                etiqueta="Agregar del catálogo"
                valor=""
                textoVacio="Buscar material o herramienta…"
                vacio={errorCatalogo ? 'No se pudo cargar el catálogo.' : 'No hay más elementos en el catálogo.'}
                alCambiar={(id) => {
                  const elemento = (catalogo ?? []).find((c) => c.id === id);
                  if (elemento) agregar(elemento);
                }}
                opciones={[...sugeridos, ...resto].map((e) => ({
                  id: e.id,
                  nombre: `${e.sugerido ? '★ ' : ''}${e.nombre} · ${ETIQUETA_TIPO[e.tipo].toLowerCase()}`,
                }))}
              />
            </div>
            <button type="button" className="boton secundario" onClick={() => setCreando(true)}>
              + Nuevo
            </button>
          </div>
        ) : null}

        {lineas.length === 0 ? (
          <p className="campo-nota" style={{ marginTop: 0 }}>
            {puedeEditar ? 'Todavía no hay materiales ni herramientas planeados.' : 'La solicitud no tiene materiales planeados.'}
          </p>
        ) : (
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Cantidad</th>
                  {puedeEditar ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {lineas.map((l, indice) => (
                  <tr key={`${l.catalogoId ?? 'nuevo'}-${l.nombre}-${indice}`}>
                    <td>
                      {l.nombre}
                      {l.catalogoId === null ? (
                        <span className="campo-nota" style={{ marginLeft: 8 }}>
                          nuevo
                        </span>
                      ) : null}
                    </td>
                    <td>{ETIQUETA_TIPO[l.tipo]}</td>
                    <td>
                      {puedeEditar ? (
                        <div className="campo" style={{ marginBottom: 0, width: 110 }}>
                          <input
                            type="number"
                            aria-label={`Cantidad de ${l.nombre}`}
                            min="0.01"
                            step="any"
                            inputMode="decimal"
                            value={l.cantidad}
                            onChange={(e) => cambiarCantidad(indice, e.target.value)}
                          />
                        </div>
                      ) : (
                        l.cantidad
                      )}
                    </td>
                    {puedeEditar ? (
                      <td style={{ textAlign: 'right' }}>
                        <button type="button" className="boton-enlace" onClick={() => quitar(indice)}>
                          Quitar
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {puedeEditar ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 14 }}>
            {guardar.isError ? <span className="campo-nota">{guardar.error.message}</span> : null}
            <button type="button" className="boton secundario" disabled={guardar.isPending} onClick={cancelarEdicion}>
              Cancelar
            </button>
            <button type="button" className="boton" disabled={!sucio || guardar.isPending} onClick={() => guardar.mutate()}>
              {guardar.isPending ? 'Guardando…' : 'Guardar plan'}
            </button>
          </div>
        ) : null}
      </div>

      {creando ? (
        <Dialogo titulo="Nuevo material o herramienta" ancho={440} alCerrar={() => setCreando(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p className="campo-nota" style={{ margin: 0 }}>
              Se agrega al plan y se registra en el catálogo al guardar el plan.
            </p>
            {errorNuevo ? <Aviso tono="atencion">{errorNuevo}</Aviso> : null}
            <div className="campo">
              <label htmlFor="nuevo-material">Nombre</label>
              <input
                id="nuevo-material"
                autoFocus
                value={nuevoNombre}
                placeholder="Ej.: Llave Allen 5 mm"
                onChange={(e) => setNuevoNombre(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    agregarNuevo();
                  }
                }}
              />
            </div>
            <SelectorSegmentado
              id="nuevo-tipo"
              etiqueta="Tipo"
              valor={nuevoTipo}
              alCambiar={(valor) => setNuevoTipo(valor as TipoLineaMaterial)}
              opciones={[
                { id: 'HERRAMIENTA', nombre: 'Herramienta' },
                { id: 'MATERIAL', nombre: 'Material' },
              ]}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="boton secundario" onClick={() => setCreando(false)}>
                Cancelar
              </button>
              <button type="button" className="boton" onClick={agregarNuevo}>
                Agregar al plan
              </button>
            </div>
          </div>
        </Dialogo>
      ) : null}
    </section>
  );
}
